// SEQ-180 phase 2, task P12: one fetch per distinct request key (plan
// decision D20), not one per widget.
//
// `usePnlAnalytics` (phase 1) calls a fixed pair of grains through two fixed
// hook calls. A dashboard's grain and range set is only known at render time
// and changes as widgets are added, so this hook runs ONE effect over the
// whole plan and keys its results by the plan's own key strings. Hooks cannot
// be called in a loop, and one effect issuing N parallel calls is also what
// keeps a 13 widget dashboard at the two calls the phase 1 page already made.
//
// Everything else follows the phase 1 hook exactly: the same 350ms debounce,
// the same DEV-only fixture fallback restricted to a MISSING RPC (never an
// authorisation or data error), and errors surfaced rather than swallowed.

import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { callPnlAnalytics } from "../../lib/pnl/analyticsClient";
import type { PnlAnalyticsFilters, PnlAnalyticsMeta, PnlAnalyticsResponse } from "../../lib/pnl/analyticsClient";
import { FIXTURE_PNL_ANALYTICS_PROJECT_RESPONSE, FIXTURE_PNL_ANALYTICS_MONTH_RESPONSE } from "./fixtures";
import type { PnlRequestKey, PnlRequestPlan } from "./dashboardRequestKeys";

const DEBOUNCE_MS = 350;

/** Same narrow match as usePnlAnalytics.ts: a missing RPC only, never a real failure. */
const RPC_MISSING_PATTERN = /does not exist|could not find the function|PGRST202/i;

function fixtureFor(groupBy: string): PnlAnalyticsResponse {
  if (groupBy === "month") return FIXTURE_PNL_ANALYTICS_MONTH_RESPONSE;
  if (groupBy === "project") return FIXTURE_PNL_ANALYTICS_PROJECT_RESPONSE;
  // Every other grain (quarter, builder, status, the two manager grains,
  // company) has no phase 1 fixture. An empty result is honest here: the
  // fixture path exists so the page can be reviewed before the RPC is
  // deployed, not to invent numbers for a grain nobody has data for.
  return { rows: [], meta: FIXTURE_PNL_ANALYTICS_PROJECT_RESPONSE.meta };
}

export interface PnlDashboardDataset {
  /** One decoded response per request key. A key missing from this map has not loaded (or failed). */
  byKey: Record<string, PnlAnalyticsResponse>;
  /**
   * React review, HIGH: one message PER KEY, not one for the render. A
   * dashboard issues up to eight independent calls (D20), so collapsing them
   * into a single error made one flaky key blank a 13 widget dashboard whose
   * other keys had already come back. The page reads this per widget and
   * leaves the rest of the grid alone.
   */
  errorByKey: Record<string, string>;
  loading: boolean;
  /**
   * Set only when EVERY key failed, which is the case a full-page error is
   * actually for. A partial failure is reported on the affected widgets.
   */
  error: string | null;
  isFixtureData: boolean;
  /** The auditability block from whichever key answered first, for the page footer. */
  meta: PnlAnalyticsMeta | null;
  refresh: () => void;
}

async function fetchKey(request: PnlRequestKey): Promise<PnlAnalyticsResponse> {
  const filters: PnlAnalyticsFilters = {
    from: request.from,
    to: request.to,
    projectIds: request.projectIds,
    statuses: request.statuses,
    // The RPC's groupBy vocabulary is checked inside callPnlAnalytics, which
    // throws a named client-side error rather than letting a stale grain
    // reach Postgres as a bare SQLSTATE 22023.
    groupBy: request.groupBy as PnlAnalyticsFilters["groupBy"],
    metrics: request.metrics,
    schemaVersion: 1,
  };
  return callPnlAnalytics(supabase, filters);
}

export function usePnlDashboardData(plan: PnlRequestPlan): PnlDashboardDataset {
  const [byKey, setByKey] = useState<Record<string, PnlAnalyticsResponse>>({});
  const [errorByKey, setErrorByKey] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFixtureData, setIsFixtureData] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  // The plan object is rebuilt every render, so the serialised key list is
  // what decides whether to refetch, exactly as usePnlAnalytics.ts compares
  // serialised filters rather than object identity.
  const planKey = useMemo(
    () => JSON.stringify(plan.keys.map((k) => [k.key, [...k.metrics].sort()])),
    [plan.keys],
  );

  useEffect(() => {
    const requests = plan.keys;
    if (requests.length === 0) {
      setByKey({});
      setErrorByKey({});
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    const handle = setTimeout(() => {
      setLoading(true);
      setError(null);

      Promise.all(
        requests.map(async (request) => {
          try {
            const response = await fetchKey(request);
            return { key: request.key, response, fixture: false as const, error: null };
          } catch (err: unknown) {
            const message = err instanceof Error ? err.message : String(err);
            if (import.meta.env.DEV && RPC_MISSING_PATTERN.test(message)) {
              return { key: request.key, response: fixtureFor(request.groupBy), fixture: true as const, error: null };
            }
            return { key: request.key, response: null, fixture: false as const, error: message };
          }
        }),
      )
        .then((results) => {
          if (cancelled) return;
          const next: Record<string, PnlAnalyticsResponse> = {};
          const nextErrors: Record<string, string> = {};
          let sawFixture = false;

          for (const result of results) {
            if (result.response) next[result.key] = result.response;
            if (result.fixture) sawFixture = true;
            if (result.error) nextErrors[result.key] = result.error;
          }

          setByKey(next);
          setErrorByKey(nextErrors);
          setIsFixtureData(sawFixture);
          // A full-page error is for the case where nothing came back at all.
          // One key failing out of several is the affected widgets' problem,
          // not the whole dashboard's.
          const everyKeyFailed = results.length > 0 && Object.keys(next).length === 0;
          setError(everyKeyFailed ? (nextErrors[results[0].key] ?? "The dashboard could not be loaded.") : null);
          setLoading(false);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          // Promise.all above never rejects (every call is caught per key), so
          // reaching here means the batch itself failed, which is a whole
          // dashboard problem rather than one widget's.
          setError(err instanceof Error ? err.message : String(err));
          setLoading(false);
        });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- setTimeout debounce window, same as usePnlAnalytics.ts
    }, DEBOUNCE_MS) as any;

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- planKey is the real dependency; plan.keys is a fresh array every render
  }, [planKey, refreshToken]);

  const refresh = useCallback(() => setRefreshToken((t) => t + 1), []);

  const meta = useMemo(() => {
    for (const request of plan.keys) {
      const response = byKey[request.key];
      if (response) return response.meta;
    }
    return null;
  }, [plan.keys, byKey]);

  return { byKey, errorByKey, loading, error, isFixtureData, meta, refresh };
}
